import { useCallback, useMemo } from 'react';
import useLocalStorage from 'react-localstorage-hook';

import Decimal from 'decimal.js';

import { DefaultFarm } from '@src/types/vaults/farm.types';
import {
  readContracts,
  simulateContract,
  waitForTransactionReceipt,
} from '@wagmi/core';
import { Address } from 'viem';
import { useReadContracts, useSimulateContract } from 'wagmi';

import { useUpdateVaultData } from '@services/api/vaults/vaults.api';

import { useAuth } from '@contexts/common/AuthorizationContext';

import { config } from '@tools/wagmi';

import { mapWagmiError } from '@helpers/common/error.helpers';
import {
  normalizePercent,
  normalizePercentForContact,
  normalizeToken,
} from '@helpers/vaults/priceRange.helpers';

import { useWriteContractExtraGas } from '@utils/contract.utils';
import { toBN } from '@utils/formaters.utils';

import showToast from '@components/common/Toast';

import { CHAIN_ID } from '@constants/common/chain.constants';
import { SLIPPAGE_TOLERANCE } from '@constants/common/localStorage.constants';
import { SuccessMessages } from '@constants/common/messages.constants';
import { ToastType } from '@constants/common/toast.constants';
import { V3_TOKENS_TYPE } from '@constants/vaults/tokens.constants';

import abi from '@abi/vault/FarmConnectorV2';

import { TokensDto } from '@dto/vaults/tokens.dto';
import { VaultDto } from '@dto/vaults/vaults.dto';

import {
  PriceRangeInfoItem,
  StakingToken,
} from '@app-types/vaults/priceRange.types';
import {
  PendingRewardsType,
  VaultFarmsType,
} from '@app-types/vaults/vault.types';
import { VaultFarmsV3ItemType } from '@app-types/vaults/vaultContext.types';

type FarmPendingRewardsParamType = {
  vault: VaultDto;
  vaultFarms: VaultFarmsType[];
  tokens: TokensDto | undefined;
  isFullInfo?: boolean;
};

export const useFarmsPendingRewards = ({
  vault,
  vaultFarms,
  tokens,
  isFullInfo = false,
}: FarmPendingRewardsParamType) => {
  const pendingRewardsCalls = useMemo(() => {
    const calls = vaultFarms.map((farm) => {
      const { connector } = farm.farm;
      const { rewardTokens } = farm.farmInfo;

      return rewardTokens.map(({ address }) => ({
        address: connector as Address,
        abi,
        functionName: 'pendingRewards' as const,
        args: [address as Address, connector as Address] as const,
        chainId: vault.chainId,
      }));
    });

    return calls.flat();
  }, [vault, vaultFarms]);

  const { data, refetch } = useReadContracts({
    allowFailure: false,
    contracts: pendingRewardsCalls,
    query: {
      enabled: isFullInfo,
    },
  });

  const farmsPendingRewards: PendingRewardsType[] = useMemo(() => {
    if (!data || !tokens) return [];

    return data.map((reward, index) => {
      const rewardTokenAddress = pendingRewardsCalls[index].args[0];
      const rewardToken =
        tokens.farm[vault.chainId][rewardTokenAddress.toLowerCase()];

      const pendingRewardsValue = toBN(reward);
      const pendingRewardsUsd = toBN(reward, rewardToken?.decimals).times(
        rewardToken?.priceInUsd,
      );

      return {
        pendingRewardsUsd: pendingRewardsUsd.isNaN()
          ? toBN(0)
          : pendingRewardsUsd,
        pendingRewards: pendingRewardsValue.isNaN()
          ? toBN(0)
          : pendingRewardsValue,
        rewardToken,
      };
    });
  }, [data, pendingRewardsCalls, vault, tokens]);

  return {
    farmsPendingRewards,
    refetch,
  };
};

interface PriceRangeInfoParamType {
  vaultFarms: VaultFarmsType[];
  isFullInfo?: boolean;
}

export const usePriceRangeInfo = ({
  vaultFarms,
  isFullInfo = false,
}: PriceRangeInfoParamType) => {
  const farmsV3 = useMemo(
    () =>
      vaultFarms.filter((farm) =>
        V3_TOKENS_TYPE.includes(farm.farmInfo.stakingTokenType),
      ),
    [vaultFarms],
  );

  const createQueries = useCallback(
    (functionName: string) => {
      if (!farmsV3.length) return [];

      return farmsV3.map((farm) => {
        const { connector } = farm.farm;
        const { chainId } = farm.farmInfo;
        return {
          address: connector as Address,
          abi,
          functionName,
          chainId,
          ...(functionName === 'liquidity' ? { args: [connector] } : {}),
        };
      });
    },
    [farmsV3],
  );

  const liquidityQueries = useMemo(
    () => createQueries('liquidity'),
    [createQueries],
  );

  const priceRangesQueries = useMemo(
    () => createQueries('priceRangesData'),
    [createQueries],
  );

  const { data, refetch } = useReadContracts({
    contracts: [
      ...createQueries('liquidity'),
      ...createQueries('priceRangesData'),
    ],
    allowFailure: false,
    query: {
      enabled:
        isFullInfo && !!liquidityQueries.length && !!priceRangesQueries.length,
    },
  });

  const { fetchedLiquidityData = [], fetchedPriceRangesData = [] } =
    useMemo(() => {
      if (!data)
        return { fetchedLiquidityData: [], fetchedPriceRangesData: [] };

      return {
        fetchedLiquidityData: data.slice(0, liquidityQueries.length),
        fetchedPriceRangesData: data.slice(liquidityQueries.length),
      };
    }, [data, liquidityQueries]);

  const amountOutQueries = useMemo(() => {
    if (!farmsV3.length || !fetchedLiquidityData.length) return [];

    return farmsV3.map((farm, index) => ({
      address: farm.farm.connector as Address,
      abi,
      functionName: 'getAmountOutStakingTokenToNative' as const,
      chainId: farm.farmInfo.chainId,
      args: [fetchedLiquidityData[index] as bigint],
    }));
  }, [farmsV3, fetchedLiquidityData]);

  const { data: fetchedAmountOut, refetch: refetchAmountOut } =
    useReadContracts({
      contracts: amountOutQueries,
      allowFailure: false,
      query: {
        enabled: isFullInfo && !!amountOutQueries.length,
      },
    });

  const groupedData: Record<number, PriceRangeInfoItem> = useMemo(() => {
    if (!fetchedPriceRangesData.length || !fetchedAmountOut?.length) return {};

    return farmsV3.reduce(
      (acc, farmV3, index) => {
        const { farmId } = farmV3.farm;
        const stakingTokenDataRaw = fetchedPriceRangesData[index];
        const amountOutStakingTokenToNative = toBN(fetchedAmountOut[index]);

        const priceRangesData = normalizeToken(
          stakingTokenDataRaw as unknown as StakingToken,
        );
        const noShiftPercent =
          toBN(priceRangesData.sqrtPriceX96ShiftPercentUp).equals(0) &&
          toBN(priceRangesData.sqrtPriceX96ShiftPercentLow).equals(0);

        const {
          sqrtPriceX96ShiftPercentUp,
          sqrtPriceX96ShiftPercentLow,
          sqrtPriceX96DefaultShiftPercentUp,
          sqrtPriceX96DefaultShiftPercentLow,
        } = priceRangesData;

        const initialMinPercent = normalizePercent(
          noShiftPercent
            ? sqrtPriceX96DefaultShiftPercentLow
            : sqrtPriceX96ShiftPercentLow,
        );
        const initialMaxPercent = normalizePercent(
          noShiftPercent
            ? sqrtPriceX96DefaultShiftPercentUp
            : sqrtPriceX96ShiftPercentUp,
        );

        acc[farmId] = {
          priceRangesData,
          amountOutStakingTokenToNative,
          initialMinPercent: -initialMinPercent,
          initialMaxPercent: +initialMaxPercent,
        };

        return acc;
      },
      {} as Record<number, PriceRangeInfoItem>,
    );
  }, [fetchedPriceRangesData, fetchedAmountOut, farmsV3]);

  const refetchAllData = useCallback(async () => {
    await Promise.all([refetch(), refetchAmountOut()]);
  }, []);

  return {
    priceRangeInfo: groupedData,
    refetch: refetchAllData,
  };
};

interface SimulatePriceRangeParams {
  selectedV3Farm: VaultFarmsType;
  minPercent: Decimal;
  maxPercent: Decimal;
  currentV3PoolInfo: VaultFarmsV3ItemType;
  isDisabled: boolean;
}

export const useSimulateUpdatePriceRanges = ({
  minPercent,
  maxPercent,
  currentV3PoolInfo,
  selectedV3Farm,
  isDisabled,
}: SimulatePriceRangeParams) => {
  const { account, chainId } = useAuth();

  const contractAddress = selectedV3Farm.farm.connector as Address;
  const low = normalizePercentForContact(minPercent);
  const up = normalizePercentForContact(maxPercent);

  const approxToRangeLimitLowPercent =
    currentV3PoolInfo.priceRangesData?.approxToRangeLimitLowPercent ||
    BigInt(0);
  const approxToRangeLimitUpPercent =
    currentV3PoolInfo.priceRangesData?.approxToRangeLimitUpPercent || BigInt(0);

  const isEnabled = useMemo(
    () =>
      !isDisabled &&
      (!!approxToRangeLimitLowPercent ||
        !!approxToRangeLimitUpPercent ||
        !!low ||
        !!up),
    [
      approxToRangeLimitLowPercent,
      approxToRangeLimitUpPercent,
      isDisabled,
      low,
      up,
    ],
  );

  const { data, isPending } = useSimulateContract({
    address: contractAddress,
    abi,
    functionName: 'updatePriceRangesShiftPercent',
    args: [
      low,
      up,
      approxToRangeLimitLowPercent,
      approxToRangeLimitUpPercent,
      BigInt(1),
      BigInt(1),
      BigInt(1),
    ],
    account,
    chainId,
    query: {
      enabled: isEnabled,
    },
  });

  const amountOutStakingTokenToNativeAdjusted = useMemo(
    () => (data ? toBN(data.result[0]) : toBN(0)),
    [data],
  );

  return {
    amountOutStakingTokenToNativeAdjusted,
    isPending: isPending && isEnabled,
  };
};

interface UseUpdatePriceRangeParams {
  vaultId: number;
  handleUpdate: () => Promise<void>;
}

interface PriceRangeParams {
  selectedV3Farm: VaultFarmsType;
  minPercent: Decimal;
  maxPercent: Decimal;
  currentV3PoolInfo: VaultFarmsV3ItemType;
}

export const useUpdatePriceRange = ({
  vaultId,
  handleUpdate,
}: UseUpdatePriceRangeParams) => {
  const { account, chainId } = useAuth();
  const { mutateAsync: updateData } = useUpdateVaultData();

  const [slippageInStorage]: [slippageInStorage: string] =
    useLocalStorage(SLIPPAGE_TOLERANCE);

  const { writeContractAsync, ...rest } = useWriteContractExtraGas({
    config,
    mutation: {
      onSuccess: async (data) => {
        const transactionReceipt = await waitForTransactionReceipt(config, {
          hash: data,
        });

        await updateData({ account: account as Address, vaultId });
        await handleUpdate();

        showToast(
          {
            title: SuccessMessages.Updated,
            withLink: {
              hash: transactionReceipt.transactionHash,
              chainId: chainId as number,
            },
          },
          ToastType.Success,
        );
      },
      onError: (e) => {
        console.log('useUpdatePriceRange:', e);
        showToast({ title: mapWagmiError(e) }, ToastType.Error);
      },
    },
  });

  const onWriteContractAsync = useCallback(
    async ({
      selectedV3Farm,
      minPercent,
      maxPercent,
      currentV3PoolInfo,
    }: PriceRangeParams) => {
      const contractAddress = selectedV3Farm.farm.connector as Address;

      const low = normalizePercentForContact(minPercent);
      const up = normalizePercentForContact(maxPercent);

      const {
        priceRangesData: {
          approxToRangeLimitLowPercent,
          approxToRangeLimitUpPercent,
        },
      } = currentV3PoolInfo;

      const minAmountOutRequest = await simulateContract(config, {
        abi,
        address: contractAddress,
        functionName: 'updatePriceRangesShiftPercent',
        chainId,
        args: [
          low,
          up,
          approxToRangeLimitLowPercent,
          approxToRangeLimitUpPercent,
          BigInt(1),
          BigInt(1),
          BigInt(1),
        ],
        account: account as Address,
      });

      const minAmountOut = toBN(String(minAmountOutRequest.result[0]))
        .times(slippageInStorage)
        .toFixed(0);
      const amount0 = toBN(String(minAmountOutRequest.result[1]))
        .times(slippageInStorage)
        .toFixed(0);
      const amount1 = toBN(String(minAmountOutRequest.result[2]))
        .times(slippageInStorage)
        .toFixed(0);

      await writeContractAsync({
        abi,
        address: contractAddress,
        functionName: 'updatePriceRangesShiftPercent',
        chainId,
        args: [
          low,
          up,
          approxToRangeLimitLowPercent,
          approxToRangeLimitUpPercent,
          BigInt(minAmountOut),
          BigInt(amount0),
          BigInt(amount1),
        ],
      });
    },
    [account, chainId, slippageInStorage, writeContractAsync],
  );

  return {
    writeContractAsync: onWriteContractAsync,
    ...rest,
  };
};

type getAmountOutStakingTokenToNativeParamType = {
  connector: Address;
  chainId: CHAIN_ID;
  amount: bigint;
};

export const getAmountOutStakingTokenToNative = async ({
  connector,
  chainId,
  amount,
}: getAmountOutStakingTokenToNativeParamType) =>
  readContracts(config, {
    contracts: [
      {
        abi,
        address: connector,
        functionName: 'getAmountOutStakingTokenToNative',
        args: [amount],
        chainId,
      },
      {
        abi,
        address: connector,
        functionName: 'native',
        chainId,
      },
    ],
    allowFailure: false,
  });

interface UseAmountOutStakingTokenToNativeParams {
  farms: DefaultFarm[];
  isDisabled: boolean;
}

export const useAmountOutStakingTokenToNative = ({
  farms,
  isDisabled,
}: UseAmountOutStakingTokenToNativeParams) => {
  const liquidityCalls = useMemo(() => {
    const calls = farms.map((farm) => {
      const { connector, chainId } = farm.farm;
      return {
        address: farm.farm.connector as Address,
        abi,
        functionName: 'liquidity' as const,
        args: [connector as Address] as const,
        chainId,
      };
    });

    return calls.flat();
  }, [farms]);

  const { data, isPending } = useReadContracts({
    allowFailure: false,
    contracts: liquidityCalls,
    query: {
      enabled: !!liquidityCalls.length || !isDisabled,
    },
  });

  const nativeAmountCalls = useMemo(() => {
    if (!data?.length) return [];
    const calls = farms.map((farm, index) => {
      const { connector, chainId } = farm.farm;
      const amountIn = data[index];

      return {
        address: connector as Address,
        abi,
        functionName: 'getAmountOutStakingTokenToNative' as const,
        args: [amountIn] as const,
        chainId,
      };
    });

    return calls.flat();
  }, [farms, data]);

  const { data: dataAmountOut, isPending: isPendingAmountOut } =
    useReadContracts({
      allowFailure: false,
      contracts: nativeAmountCalls,
      query: {
        enabled: !!nativeAmountCalls.length,
      },
    });

  const amountInNative = useMemo(() => {
    if (!dataAmountOut?.length) return toBN(0);
    return dataAmountOut.reduce((acc, curr) => acc.plus(toBN(curr)), toBN(0));
  }, [dataAmountOut]);

  return {
    amountInNative,
    isPending: isPending || isPendingAmountOut,
  };
};
